import { useState } from 'react';

export default function MembershipForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    birthdate: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    industry: '',
    address: '',
    education: '',
    referrer: '',
    motivation: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setIsSuccess(false);

    try {
      const submitData = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d61tgt150i17hner1rh0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setIsSuccess(true);
        setSubmitMessage('申請已送出！我們會盡快與您聯繫。');
        setFormData({
          name: '',
          gender: '',
          birthdate: '',
          email: '',
          phone: '',
          company: '',
          position: '',
          industry: '',
          address: '',
          education: '',
          referrer: '',
          motivation: ''
        });
      } else {
        setIsSuccess(false);
        setSubmitMessage('送出失敗，請稍後再試。');
      }
    } catch (error) {
      setIsSuccess(false);
      setSubmitMessage('發生錯誤，請稍後再試。');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <section id="membership" className="py-16 sm:py-24 bg-gradient-to-br from-purple-50 via-white to-orange-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 rounded-full mb-4 sm:mb-6">
            <i className="ri-user-add-line text-purple-900 w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
            <span className="text-xs sm:text-sm font-medium text-purple-900">Join Us</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            加入會員
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            歡迎加入台北青企社，與優秀的青年企業家一起成長、交流與服務社會
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-5 sm:p-8 md:p-12">
          <form id="membership-form" onSubmit={handleSubmit} data-readdy-form>
            <div className="space-y-6 sm:space-y-8">
              {/* 基本資料 */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                  <div className="w-7 sm:w-8 h-7 sm:h-8 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-white text-xs sm:text-sm w-3 sm:w-4 h-3 sm:h-4 flex items-center justify-center"></i>
                  </div>
                  <span>基本資料</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                      placeholder="請輸入您的姓名"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      性別 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                    >
                      <option value="">請選擇</option>
                      <option value="男">男</option>
                      <option value="女">女</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      出生日期 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="birthdate"
                      value={formData.birthdate}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      學歷 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="education"
                      value={formData.education}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                    >
                      <option value="">請選擇</option>
                      <option value="高中職">高中職</option>
                      <option value="大學">大學</option>
                      <option value="碩士">碩士</option>
                      <option value="博士">博士</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 聯絡資訊 */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                  <div className="w-7 sm:w-8 h-7 sm:h-8 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-xs sm:text-sm w-3 sm:w-4 h-3 sm:h-4 flex items-center justify-center"></i>
                  </div>
                  <span>聯絡資訊</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      電子郵件 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      聯絡電話 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                      placeholder="0912-345-678"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      聯絡地址 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                      placeholder="請輸入完整地址"
                    />
                  </div>
                </div>
              </div>

              {/* 公司資訊 */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                  <div className="w-7 sm:w-8 h-7 sm:h-8 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-white text-xs sm:text-sm w-3 sm:w-4 h-3 sm:h-4 flex items-center justify-center"></i>
                  </div>
                  <span>公司資訊</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      公司名稱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                      placeholder="請輸入公司名稱"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      職稱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                      placeholder="例：總經理、執行長"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      產業類別 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                    >
                      <option value="">請選擇產業類別</option>
                      <option value="科技業">科技業</option>
                      <option value="製造業">製造業</option>
                      <option value="服務業">服務業</option>
                      <option value="金融業">金融業</option>
                      <option value="貿易業">貿易業</option>
                      <option value="建築業">建築業</option>
                      <option value="醫療業">醫療業</option>
                      <option value="教育業">教育業</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 其他資訊 */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                  <div className="w-7 sm:w-8 h-7 sm:h-8 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-text-line text-white text-xs sm:text-sm w-3 sm:w-4 h-3 sm:h-4 flex items-center justify-center"></i>
                  </div>
                  <span>其他資訊</span>
                </h3>
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      推薦人
                    </label>
                    <input
                      type="text"
                      name="referrer"
                      value={formData.referrer}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm"
                      placeholder="若有推薦人請填寫姓名"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      入會動機 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-base sm:text-sm resize-none"
                      placeholder="請簡述您的入會動機與期望（限500字）"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.motivation.length}/500
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Message */}
              {submitMessage && (
                <div className={`p-3 sm:p-4 rounded-lg ${
                  isSuccess
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <i className={`${
                      isSuccess ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
                    } text-base sm:text-lg w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center flex-shrink-0`}></i>
                    <span className="text-xs sm:text-sm font-medium">{submitMessage}</span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-center pt-2 sm:pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-8 sm:px-12 py-3.5 sm:py-4 bg-gradient-to-r from-purple-600 to-orange-600 text-white rounded-lg font-medium hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-base sm:text-base cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
                      送出中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
                      送出申請
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Info Box */}
        <div className="mt-6 sm:mt-8 bg-purple-50 border border-purple-200 rounded-lg sm:rounded-xl p-4 sm:p-6">
          <div className="flex gap-3 sm:gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 sm:w-10 h-8 sm:h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <i className="ri-information-line text-white w-4 sm:w-5 h-4 sm:h-5 flex items-center justify-center"></i>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1.5 sm:mb-2 text-sm sm:text-base">入會須知</h4>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-0.5 sm:space-y-1">
                <li>• 申請人年齡需滿18歲</li>
                <li>• 經審核通過後，將由專人與您聯繫</li>
                <li>• 如有任何問題，歡迎透過 官方Line聯絡我們</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
